import { Dialog, DialogPanel } from '@headlessui/react';

import ExceptionIcon from '~/shared/assets/icons/caution-red.svg';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/ButtonsV2';

import { useVolumeExclusionGuard } from '~/fleet-manager/hooks/volumeExclusions/useVolumeExclusionGuard';
import { useRemoveException } from '~/fleet-manager/hooks/volumeExclusions/useVolumeExclusionMutation';

interface RemoveExceptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  volumeId: string;
  accountId?: string;
  onSuccess?: () => void;
}

export default function RemoveExceptionModal({
  isOpen,
  onClose,
  volumeId,
  accountId,
  onSuccess,
}: RemoveExceptionModalProps) {
  const { isMissingOrgAccount } = useVolumeExclusionGuard(accountId);
  const { mutate: removeException, isPending } = useRemoveException();

  const handleRemoveException = () => {
    removeException(
      { volumeId, accountId },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      }
    );
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      as="div"
      className="relative z-[99] focus:outline-none auto-scale-rule-dialog"
      onClose={handleClose}
    >
      <div
        className="fixed inset-0 p-4 w-screen overflow-y-auto bg-[#00000059] backdrop-blur-[6px] auto-scale-rule-dialog-backdrop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <DialogPanel
            transition
            className="w-full max-w-[689px] rounded-xl bg-white p-6 backdrop-blur-2xl duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="flex flex-col w-full items-center">
              <ExceptionIcon className="size-11.5" />
              <Typography
                variant="H3-med"
                fontWeight={600}
                className="mt-6 font-family-outfit text-center"
              >
                Are you sure you want to remove the exception for this volume?
              </Typography>
              <Typography variant="p3-reg" className="mt-2 max-w-[536px] text-center">
                Autoscaling will be activated for this volume
              </Typography>

              <div className="flex gap-3 mt-6 pt-6">
                <Button
                  variant="third"
                  className="px-7.5 py-3"
                  onClick={handleClose}
                >
                  <Typography variant="p3" fontWeight={600} color="text-primary-900">
                    No, cancel
                  </Typography>
                </Button>
                <Button
                  className="px-7.5 py-3 min-w-54 font-semibold"
                  onClick={handleRemoveException}
                  disabled={isPending || isMissingOrgAccount}
                  loading={isPending}
                >
                  <Typography variant="p3" fontWeight={600} color="text-white">
                    Yes, remove exception
                  </Typography>
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
